import qs from 'qs';
import { z } from 'zod';

type FilterScalar = string | number | boolean | null | undefined;

type FilterParam = FilterScalar | FilterParam[] | { [key: string]: FilterParam };

type FilterVariable = FilterScalar | readonly FilterScalar[];

const filterParamSchema: z.ZodType<FilterParam, z.ZodTypeDef, unknown> = z.lazy(() =>
  z.union([
    z.literal('true').transform(() => true),
    z.literal('false').transform(() => false),
    z.string().transform((value) => (value.includes('.') ? value.split('.') : value)),
    z.array(filterParamSchema),
    z.record(filterParamSchema),
    z.number(),
    z.boolean(),
    z.null(),
    z.undefined(),
  ]),
);

const toSortFilter = (sort: string) => {
  const [field, direction] = sort.split('.');

  return { [field]: direction?.toUpperCase() ?? '' };
};

const searchParamsSchema = z
  .object({
    pageNumber: z.coerce.number().default(1),
    resultsPerPage: z.coerce.number().optional(),
    sort: z.string().optional().catch(undefined),
    to: z.string().optional(),
    from: z.string().optional(),
    fullResults: z.boolean().optional(),
  })
  .catchall(z.any());

export const parseFiltersFromSearchParams = <T>(searchParams: URLSearchParams): T => {
  const params = searchParamsSchema.parse(qs.parse(searchParams.toString(), { ignoreQueryPrefix: true }));
  const filters = z.record(filterParamSchema).parse(params);

  if (params.sort) {
    Object.assign(filters, toSortFilter(params.sort));
  }

  // SAFETY: the URL filters are written by stringifyFiltersToSearchParams and SmartTable from T; the API validates the variables.
  return filters as T;
};

export const stringifyFiltersToSearchParams = (filters: Record<string, FilterVariable>): string => {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined) continue;

    if (Array.isArray(value)) {
      params.set(key, value.join('.'));
    } else {
      params.set(key, String(value));
    }
  }

  return params.toString();
};
